import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Loader, useToast } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { api, listResource } from '@/lib/api';
import { groupFormSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';

interface GroupForm {
  name: string;
  code: string;
  description?: string;
  parentId?: string;
  isActive?: boolean;
}

interface GroupDetail {
  id: string;
  name: string;
  code?: string | null;
  description?: string | null;
  parentId?: string | null;
  isActive?: boolean;
  children?: { id: string }[];
}

interface GroupOption {
  id: string;
  name: string;
  parentId?: string | null;
}

const LABEL = 'text-sm font-medium text-dash-on-surface';
const FIELD =
  'w-full rounded-lg border border-dash-outline-variant bg-white px-4 py-3 text-base text-dash-on-surface outline-none transition-all focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20';

export function GroupFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isEdit = Boolean(id);

  const { data: existing, isLoading } = useQuery({
    queryKey: ['groups', id],
    queryFn: () => api<GroupDetail>(`/groups/${id}`),
    enabled: isEdit,
  });

  const { data: groupsData } = useQuery({
    queryKey: ['groups'],
    queryFn: () => listResource<GroupOption>('/groups'),
  });

  const parentGroups = useMemo(() => {
    const rows = groupsData?.data ?? [];
    return rows.filter((g) => !g.parentId && g.id !== id);
  }, [groupsData?.data, id]);

  const hasChildren = (existing?.children?.length ?? 0) > 0;

  const { register, handleSubmit } = useForm<GroupForm>({
    values: existing
      ? {
          name: existing.name,
          code: existing.code ?? '',
          description: existing.description ?? '',
          parentId: hasChildren ? '' : (existing.parentId ?? ''),
          isActive: existing.isActive !== false,
        }
      : undefined,
    defaultValues: {
      name: '',
      code: '',
      description: '',
      parentId: '',
      isActive: true,
    },
  });

  const mutation = useMutation({
    mutationFn: (values: GroupForm) => {
      const body = {
        name: values.name.trim(),
        code: values.code.trim(),
        description: values.description?.trim() || undefined,
        parentId: hasChildren ? null : values.parentId || null,
        ...(isEdit ? { isActive: values.isActive } : {}),
      };
      return isEdit
        ? api(`/groups/${id}`, { method: 'PATCH', body })
        : api('/groups', { method: 'POST', body });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast(groupFormSuccessToast(isEdit));
      navigate(isEdit ? `/grupos/${id}` : '/grupos');
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo guardar el grupo'));
    },
  });

  return (
    <div className="lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex items-start gap-4">
        <HubBackLink
          to={isEdit ? `/grupos/${id}` : '/grupos'}
          label={isEdit ? 'Volver al detalle del grupo' : 'Volver a grupos'}
        />
        <div>
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">
            {isEdit ? 'Editar grupo' : 'Nuevo grupo'}
          </h1>
          <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
            {isEdit
              ? 'Actualizar los datos del grupo en el sistema'
              : 'Crear un grupo para organizar usuarios y asignaciones'}
          </p>
        </div>
      </div>

      {isEdit && isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : (
        <div className="lms-credentials-glass-panel rounded-xl border border-dash-outline-variant p-6 shadow-sm md:p-8">
          <form method="post" className="space-y-6" onSubmit={handleSubmit((v) => mutation.mutate(v))}>
            <div className="space-y-2">
              <label htmlFor="name" className={LABEL}>
                Nombre <span className="text-error-600">*</span>
              </label>
              <input
                id="name"
                type="text"
                required
                placeholder="Ej. Departamento de Ventas"
                className={FIELD}
                {...register('name', { required: true })}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="code" className={LABEL}>
                Código <span className="text-error-600">*</span>
              </label>
              <input
                id="code"
                type="text"
                required
                placeholder="Ej. VENTAS"
                className={FIELD}
                {...register('code', { required: true })}
              />
              <p className="text-xs text-dash-on-surface-variant">
                Debe ser único. Si otro grupo ya lo usa, el guardado se rechaza.
              </p>
            </div>

            <div className="space-y-2">
              <label htmlFor="parent_id" className={LABEL}>
                Grupo padre
              </label>
              <div className="relative">
                <select
                  id="parent_id"
                  disabled={hasChildren}
                  className={`${FIELD} cursor-pointer appearance-none pr-10 disabled:cursor-not-allowed disabled:opacity-70`}
                  {...register('parentId')}
                >
                  <option value="">Ninguno</option>
                  {parentGroups.map((parent) => (
                    <option key={parent.id} value={parent.id}>
                      {parent.name}
                    </option>
                  ))}
                </select>
                <span
                  className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-dash-on-surface-variant"
                  aria-hidden="true"
                >
                  <span className="material-symbols-outlined">expand_more</span>
                </span>
              </div>
              <p className="text-xs text-dash-on-surface-variant">
                {hasChildren
                  ? 'Este grupo tiene subgrupos, así que permanece en el nivel raíz.'
                  : 'Solo un grupo raíz puede ser padre. El árbol tiene un nivel.'}
              </p>
            </div>

            <div className="space-y-2">
              <label htmlFor="description" className={LABEL}>
                Descripción
              </label>
              <textarea
                id="description"
                rows={4}
                placeholder="Añade detalles adicionales sobre este grupo..."
                className={`${FIELD} resize-none`}
                {...register('description')}
              />
            </div>

            {isEdit && (
              <div className="rounded-lg border border-dash-outline-variant bg-secondary-50/50 px-4 py-4">
                <label className="flex cursor-pointer items-center gap-3">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-dash-outline-variant text-dash-primary focus:ring-dash-secondary/20"
                    {...register('isActive')}
                  />
                  <span className="text-sm font-medium text-dash-on-surface">Grupo activo</span>
                </label>
              </div>
            )}

            <footer className="flex flex-col justify-end gap-4 border-t border-dash-outline-variant pt-6 sm:flex-row">
              <Link
                to={isEdit ? `/grupos/${id}` : '/grupos'}
                className="inline-flex items-center justify-center rounded-lg border border-dash-outline-variant px-8 py-3 text-sm font-medium text-dash-primary transition-colors hover:bg-secondary-100"
              >
                Cancelar
              </Link>
              <button
                type="submit"
                disabled={mutation.isPending}
                className="inline-flex items-center justify-center rounded-lg bg-dash-primary px-8 py-3 text-sm font-medium text-white shadow-sm transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-60"
              >
                {mutation.isPending ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Crear grupo'}
              </button>
            </footer>
          </form>
        </div>
      )}
    </div>
  );
}
